import { type Change, Connections, Grant, type Group, type Scope } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Choices, Status, TechnicalDetails } from '@beyond-js/ui/react';

/** The tone of each effect's tag: only what can cost money is said in the warning tone */
const TONES: Record<Group, 'neutral' | 'warning'> = { read: 'neutral', change: 'neutral', publish: 'neutral', cost: 'warning', other: 'neutral' };

/**
 * Permissions as a person chooses them: one framed group per effect (See, Change, Reach outside
 * Beyond, Spend, Other), each permission named by its product's title with its words under it, its effect
 * as a tag at the row's end, and what the decision does to it (`marks`). A permission the product no
 * longer describes is said so, its name kept in technical details; words only in English say it.
 */
export function Permissions({ scopes, chosen, onChange, product, marks = {}, disabled = false, label }: {
	scopes: Scope[];
	chosen: string[];
	onChange: (chosen: string[]) => void;
	product: string;
	/** What the decision does to a permission, said under its words */
	marks?: Record<string, Change | undefined>;
	disabled?: boolean;
	/** Names the groups for assistive technology when several lists stand on one page */
	label?: string;
}) {
	const { text, language } = usePresence();
	const groups = Grant.grouped(scopes);
	return (
		<div className="flex flex-col gap-4" aria-label={label}>
			{groups.map(({ group, scopes: members }) => {
				const names = new Set(members.map(scope => scope.name));
				return (
					<Choices
						key={group}
						framed
						legend={text(`permissions.group.${group}`)}
						value={chosen.filter(name => names.has(name))}
						onChange={(next: string[]) => onChange([...chosen.filter(name => !names.has(name)), ...next])}
						options={members.map(scope => ({
							value: scope.name,
							label: Connections.title(scope, language) ?? text('permissions.undescribed', { product }),
							hint: <Hint scope={scope} mark={marks[scope.name]} />,
							status: scope.effect ? [text(`permissions.effect.${scope.effect}`), TONES[group]] : null,
							disabled
						}))}
					/>
				);
			})}
		</div>
	);
}

/** A permission's words, the language they are in when it is not the person's, and what the decision does to it */
function Hint({ scope, mark }: { scope: Scope; mark?: Change }) {
	const { text, language } = usePresence();
	const words = Connections.words(scope, language);
	const titled = Boolean(scope.title);
	return (
		<span className="flex flex-col gap-1">
			{words && titled ? <span>{words}</span> : null}
			{Connections.foreign(scope, language) ? <span>{text('permissions.english')}</span> : null}
			{words ? null : <TechnicalDetails text={text('permissions.name', { name: scope.name })} locale={language} labels={TechnicalDetails.labels?.[language]} />}
			{mark === 'add' ? <Status tone="success" label={text('permissions.mark.add')} /> : null}
			{mark === 'remove' ? <Status tone="danger" label={text('permissions.mark.remove')} /> : null}
		</span>
	);
}

/** A list of permissions in a sentence, by their titles in the person's language */
export function listed(scopes: Scope[], language: string, fallback: string): string {
	const names = scopes.map(scope => Connections.title(scope, language) ?? fallback);
	return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(names);
}
